import type { ReactNode } from "react";
import { ArrowDownAZ } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/shared/empty-state";
import { ObjectiveCard } from "@/features/objectives/objective-card";
import { ObjectiveFiltersBar } from "@/features/objectives/objective-filters";
import { ObjectiveSummaryStrip } from "@/features/objectives/objective-summary-strip";
import { ObjectiveTable } from "@/features/objectives/objective-table";
import { ObjectivesViewToggle } from "@/features/objectives/objectives-view-toggle";
import { formatRelativeTime } from "@/lib/date";
import type { ObjectiveFilters } from "@/hooks/use-objectives";
import type { Objective } from "@/types/entities";
import type { ObjectiveLadder } from "@/lib/objective-ladder";
import type { ObjectivesView } from "@/router";

interface ObjectivesListShellProps {
  headerTitle: string;
  headerActions?: ReactNode;
  showSubtitle: boolean;
  showSummary: boolean;
  filters: ObjectiveFilters;
  onFilterChange: (filters: ObjectiveFilters) => void;
  view: ObjectivesView;
  onViewChange: (next: ObjectivesView) => void;
  objectives: Objective[];
  ladderMap: Map<string, ObjectiveLadder | null>;
  isLoading: boolean;
  error?: { message: string } | null;
  nextCursor?: string | null;
  onLoadMore?: () => void;
  emptyTitle: string;
  emptyDescription: string;
  emptyAction?: ReactNode;
}

function ObjectiveListSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="rounded-lg border p-6 space-y-3">
          <div className="flex justify-between">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-5 w-20" />
          </div>
          <Skeleton className="h-4 w-full" />
          <div className="flex gap-2">
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-5 w-24" />
          </div>
        </div>
      ))}
    </div>
  );
}

function computeLastUpdatedAt(objectives: Objective[]): string | null {
  let latest: string | null = null;
  for (const obj of objectives) {
    if (!obj.updated_at) continue;
    if (!latest || Date.parse(obj.updated_at) > Date.parse(latest)) {
      latest = obj.updated_at;
    }
  }
  return latest;
}

export function ObjectivesListShell({
  headerTitle,
  headerActions,
  showSubtitle,
  showSummary,
  filters,
  onFilterChange,
  view,
  onViewChange,
  objectives,
  ladderMap,
  isLoading,
  error,
  nextCursor,
  onLoadMore,
  emptyTitle,
  emptyDescription,
  emptyAction,
}: ObjectivesListShellProps) {
  const lastUpdatedAt = computeLastUpdatedAt(objectives);
  const subtitleText =
    showSubtitle && objectives.length > 0
      ? `${objectives.length} ${objectives.length === 1 ? "objective" : "objectives"}` +
        (lastUpdatedAt ? ` · last updated ${formatRelativeTime(lastUpdatedAt)}` : "")
      : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold">{headerTitle}</h1>
          {subtitleText && (
            <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{subtitleText}</p>
          )}
        </div>
        {headerActions && <div className="flex items-center gap-2">{headerActions}</div>}
      </div>

      {showSummary && objectives.length > 0 && (
        <ObjectiveSummaryStrip objectives={objectives} />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <ObjectiveFiltersBar filters={filters} onFilterChange={onFilterChange} />
        <div className="ml-auto">
          <ObjectivesViewToggle value={view} onChange={onViewChange} />
        </div>
      </div>

      {objectives.length > 0 && !isLoading && (
        <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
          <span>
            Showing <strong className="font-semibold text-[hsl(var(--foreground))]">{objectives.length}</strong>{" "}
            objectives · Sorted by recently updated
          </span>
          <span className="ml-auto inline-flex items-center gap-1">
            <ArrowDownAZ className="h-3 w-3" aria-hidden="true" />
            Sort
          </span>
        </div>
      )}

      {isLoading ? (
        <ObjectiveListSkeleton />
      ) : error ? (
        <EmptyState title="Failed to load objectives" description={error.message} />
      ) : objectives.length === 0 ? (
        <EmptyState title={emptyTitle} description={emptyDescription}>
          {emptyAction}
        </EmptyState>
      ) : (
        <>
          {view === "table" ? (
            <ObjectiveTable objectives={objectives} ladderMap={ladderMap} />
          ) : (
            <div className="space-y-4">
              {objectives.map((objective) => (
                <ObjectiveCard
                  key={objective.id}
                  objective={objective}
                  ladder={ladderMap.get(objective.id)}
                />
              ))}
            </div>
          )}

          {nextCursor && onLoadMore && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={onLoadMore}>
                Load More
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
